/*
 * Third-party locale data for the active language: AG Grid's built-in texts (filter menus,
 * pagination, column tool panel, ...) and the month/day names of both date pickers.
 * Loaded once at start-up, after initI18n(), and only for non-English languages.
 *
 * Date *formats* are not touched: they come from the tenant's date-format setting and the
 * pickers keep parsing/serialising with it. Only the names shown in the calendars change.
 */
import $ from 'jquery';
import 'jquery-datetimepicker';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { SOURCE_LANGUAGE } from './languages';
import { translateOnce } from './index';

let agGridLocaleText;

const AG_GRID_EXPORT = { it: 'AG_GRID_LOCALE_IT', fr: 'AG_GRID_LOCALE_FR', de: 'AG_GRID_LOCALE_DE', ar: 'AG_GRID_LOCALE_EG' };

const DATE_FNS_LOCALES = {
    it: () => import('date-fns/locale/it'),
    fr: () => import('date-fns/locale/fr'),
    de: () => import('date-fns/locale/de'),
    ar: () => import('date-fns/locale/ar'),
};

function loadAgGridLocale(lng) {
    const name = AG_GRID_EXPORT[lng];
    if (!name) return Promise.resolve(undefined);
    // One lazy chunk holding only the exports listed here (the package bundles ~30 languages in one module).
    return import(
        /* webpackChunkName: "ag-grid-locale" */
        /* webpackExports: ["AG_GRID_LOCALE_IT", "AG_GRID_LOCALE_FR", "AG_GRID_LOCALE_DE", "AG_GRID_LOCALE_EG"] */
        '@ag-grid-community/locale'
    ).then(m => m[name]);
}

async function loadDatePickerLocales(lng) {
    // jquery-datetimepicker ships its own translations keyed by language code.
    if ($.datetimepicker && typeof $.datetimepicker.setLocale === 'function') $.datetimepicker.setLocale(lng);
    const loader = DATE_FNS_LOCALES[lng];
    if (loader) {
        const mod = await loader();
        registerLocale(lng, mod.default || mod);
        setDefaultLocale(lng);
    }
}

export async function loadLocaleData(lng) {
    if (lng === SOURCE_LANGUAGE) return;
    const [grid] = await Promise.all([
        loadAgGridLocale(lng).catch(e => { console.warn('[i18n] AG Grid locale not loaded', e); return undefined; }),
        loadDatePickerLocales(lng).catch(e => console.warn('[i18n] date picker locale not loaded', e)),
    ]);
    agGridLocaleText = grid;
}

/**
 * AG Grid `localeText` for the active language (undefined in English = AG Grid defaults).
 * Keys the package does not cover fall back to AG Grid's English text.
 */
export function getAgGridLocaleText() {
    return agGridLocaleText;
}

/**
 * AG Grid `headerValueGetter` that translates a literal `headerName` for display only.
 * colDef.headerName itself stays in English because code compares it (e.g. `colDef.headerName === "Comments"`)
 * and grid states / saved views reference it. Most headerNames are already translated with getLabel;
 * translateOnce leaves those alone.
 */
export function translatedHeaderValueGetter(params) {
    const colDef = params.colDef || {};
    return translateOnce(colDef.headerName);
}
